import { historicalDetailError } from './detail-resources.js';
// Revenue 分析（Phase 2 v2）
//
// v2 在 v1 上加了：
//   - 结算状态过滤胶囊（全部 / PENDING / SETTLED）
//   - 列头排序（createdAt / netAmount）
//   - 翻页（后端 page/limit + Load more）
//   - endpoint 拆解表 row 上加了 method pill
//
// 数据源：
// - 概览卡：/provider/dashboard/apis (plural) 取这一行的服务级金额
// - 收入时序：/provider/dashboard/earnings/trend
// - 按 endpoint 拆解：/provider/dashboard/apis/:apiId (endpoints)
// - 结算明细：/provider/dashboard/earnings/history (filter by apiServiceId + status)

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import { exactComparisonBounds } from './analytics-window.js';
import { useT } from '../../i18n.jsx';
import {
  KpiCard,
  KpiGrid,
  RangePicker,
  Section,
  ChartGrid,
  BarChart,
  LineChart,
  EmptyBarChart,
  EmptyTable,
  LoadingHint,
  FilterBar,
  PillGroup,
  ExportCsvButton,
  SortHeader,
  SettlementPill,
  MethodPill,
  DataTable,
  TableFooter,
  fmtNum,
  fmtUsd,
  fmtTinyUsd,
  fmtUsdExact,
} from './analytics-shared.jsx';

const STATUS_OPTIONS = [
  { v: null, l: '全部' },
  { v: 'PENDING', l: '待结算' },
  { v: 'SETTLED', l: '已结算' },
];
const PAGE_SIZE = 20;

export const RevenueSection = ({ api }) => {
  const { authed, user } = XShell.useAuth();
  const owner = user?._raw?.id ?? user?.id ?? null;
  return <RevenueState key={JSON.stringify([Boolean(authed), owner, api?.id])} api={authed ? api : null} />;
};

const RevenueState = ({ api }) => {
  const { t } = useT();
  const { navigate } = XShell.useRoute();
  const apiId = api?.id;
  const historyCursor = useRef(null);
  const historyGeneration = useRef(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  // Freeze rolling bounds across renders and appended pages. Only the picker
  // changes the window; every request receives exactly these same instants.
  const bounds = useMemo(() => exactComparisonBounds(range, customRange), [range, customRange.from, customRange.to]);
  const historyInFlight = useRef(null);

  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [detail, setDetail] = useState(null);
  const [apiSummary, setApiSummary] = useState(null);
  const [callersRevenue, setCallersRevenue] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewReload, setOverviewReload] = useState(0);

  // 结算明细独立 state
  const [statusFilter, setStatusFilter] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historySort, setHistorySort] = useState({ key: 'createdAt', dir: 'desc' });

  useEffect(() => {
    setOverview(null); setTrend(null); setDetail(null); setApiSummary(null); setCallersRevenue(null);
    setOverviewError(null);
    if (!apiId || !bounds) { setOverviewLoading(false); return; }
    let cancelled = false;
    setOverviewLoading(true);
    Promise.all([
      XApi.providerDashboardService.overview(),
      XApi.providerDashboardService.revenueAnalysis(apiId, { startDate: bounds.from, endDate: bounds.to }),
    ]).then(([o, revenue]) => {
      if (cancelled) return;
      setOverview(o); setTrend(revenue.trend); setDetail(revenue.detail);
      setApiSummary(revenue.apiSummary); setCallersRevenue(revenue.callersRevenue);
    }).catch(e => { if (!cancelled) setOverviewError(historicalDetailError(e) ?? (e?.message?.includes('UTC') ? e.message : '收入分析加载失败，请重试')); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [apiId, bounds, overviewReload]);

  // 拉一页结算明细。append=true 时把当前数据 concat 到列表底部（翻页）。
  const fetchHistory = useCallback(async ({ page, append = false }) => {
    if (!apiId || !bounds) return;
    if (append && (historyInFlight.current || !historyCursor.current)) return;
    if (!append) {
      historyGeneration.current++; historyCursor.current = null;
      setHistoryItems([]); setHistoryTotal(0); setHistoryTotalPages(0); setHistoryPage(1); setHistoryHasMore(false);
    }
    const generation = historyGeneration.current;
    const request = {}; historyInFlight.current = request;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await XApi.providerDashboardService.earningsHistory({
        apiServiceId: apiId,
        startDate: bounds.from,
        endDate: bounds.to,
        page,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        cursor: append ? historyCursor.current : undefined,
      });
      if (generation !== historyGeneration.current) return;
      historyCursor.current = res.nextCursor ?? null;
      setHistoryHasMore(res.hasMore ?? page < (res.totalPages ?? 0));
      setHistoryItems((prev) => (append ? [...prev, ...(res.items || [])] : (res.items || [])));
      setHistoryTotal(res.total || 0);
      setHistoryTotalPages(res.totalPages || 0);
      setHistoryPage(page);
    } catch {
      if (generation === historyGeneration.current) setHistoryError('结算明细加载失败，请重试');
    } finally {
      if (historyInFlight.current === request) historyInFlight.current = null;
      if (generation === historyGeneration.current) setHistoryLoading(false);
    }
  }, [apiId, statusFilter, bounds]);

  // Status or any exact boundary change invalidates all pages and pending reads.
  useEffect(() => {
    setHistoryItems([]); setHistoryTotal(0); setHistoryTotalPages(0); setHistoryPage(1); setHistoryHasMore(false);
    historyCursor.current = null; historyInFlight.current = null;
    setHistoryLoading(false); setHistoryError(null);
    fetchHistory({ page: 1, append: false });
    return () => { historyGeneration.current++; };
  }, [fetchHistory]);

  const onSort = (key) =>
    setHistorySort((s) => ({ key, dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : 'desc' }));

  const sortedHistory = useMemo(() => {
    const arr = [...historyItems];
    const { key, dir } = historySort;
    arr.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (key === 'createdAt' || key === 'settledAt') {
        return dir === 'asc'
          ? new Date(av).getTime() - new Date(bv).getTime()
          : new Date(bv).getTime() - new Date(av).getTime();
      }
      return dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [historyItems, historySort]);

  if (!apiId) return null;

  const trendPoints = (trend || []).map((d) => ({ ts: d.date, value: Number(d.netEarnings) || 0 }));
  // 累计版本：同一组 ts，value 滚动相加。给「累计净收入」线图用。
  const cumulativeTrend = (() => {
    let acc = 0;
    return trendPoints.map((p) => ({ ts: p.ts, cum: (acc += p.value) }));
  })();

  const apiNet = apiSummary?.netEarnings ?? 0;
  const apiGross = apiSummary?.grossEarnings ?? 0;
  const apiFees = apiSummary?.platformFees ?? 0;
  const apiCalls = apiSummary?.totalCalls ?? 0;
  const apiCallers = apiSummary?.uniqueCallers ?? 0;
  const arpc = apiCallers > 0 ? apiNet / apiCallers : 0;
  const arpCall = apiCalls > 0 ? apiNet / apiCalls : 0;

  const hasMore = historyHasMore;

  return (
    <div style={{ padding: '24px 32px 64px' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
            Revenue
          </h2>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
            收入时序 · 按 endpoint 拆解 · 单次调用经济 · 结算明细（带筛选 / 排序 / 翻页）
          </div>
        </div>
        <RangePicker
          value={range}
          onChange={setRange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      {range === '90d' && <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:10}}>90 天及对比期按完整 UTC 小时对齐，包含当前正在进行的小时。</div>}
      {!bounds && <div role="alert">日期范围必须完整、有效、正序且不超过 365 天（UTC）</div>}
      {overviewError && <div role="alert">{overviewError} <button onClick={() => setOverviewReload(n => n + 1)}>重试收入分析</button></div>}
      <KpiGrid>
        <KpiCard
          label="该 API 净收入"
          value={overviewLoading ? '…' : fmtTinyUsd(apiNet)}
          dim={!overviewLoading && apiNet === 0}
          trend={apiSummary ? { current: apiNet, previous: apiSummary.prevNetEarnings ?? 0 } : null}
        />
        <KpiCard
          label="毛收入"
          value={overviewLoading ? '…' : fmtTinyUsd(apiGross)}
          dim={!overviewLoading && apiGross === 0}
          trend={apiSummary ? { current: apiGross, previous: apiSummary.prevGrossEarnings ?? 0 } : null}
        />
        {/* I-fix: hint reads as a full Chinese sentence (「占毛收入 20.0%」)
            instead of "20.0% rate" which crammed % and the English word
            "rate" against the value with no visual separation. */}
        {/* J-fix: trend arrow on 平台费 derived from prevGross − prevNet.
            Backend doesn't expose prevPlatformFee directly but the two
            adjacent fields encode it: fees = gross − net. */}
        <KpiCard
          label="平台费"
          value={overviewLoading ? '…' : fmtTinyUsd(apiFees)}
          hint={apiGross > 0 ? `占毛收入 ${((apiFees / apiGross) * 100).toFixed(1)}%` : ''}
          dim={!overviewLoading && apiFees === 0}
          trend={
            apiSummary
              ? {
                  current: apiFees,
                  previous: (apiSummary.prevGrossEarnings ?? 0) - (apiSummary.prevNetEarnings ?? 0),
                  // higher platform fee isn't strictly "good for provider"
                  // but it's a function of gross — keep arrow neutral by
                  // letting it inherit the gross direction. higherIsBetter
                  // stays default (true) since net is what matters and
                  // they're proportional.
                }
              : null
          }
        />
        <KpiCard label="ARPU" value={overviewLoading ? '…' : fmtTinyUsd(arpc)} hint="单调用方均收入" dim={!overviewLoading && arpc === 0} />
        {/* J-fix: single-call price trend = prevNet / prevCalls. Meaningful
            because it tells the provider "are we extracting more per call
            over time" (e.g. higher-priced endpoints getting more traffic). */}
        <KpiCard
          label="单次调用均价"
          value={overviewLoading ? '…' : fmtTinyUsd(arpCall)}
          dim={!overviewLoading && arpCall === 0}
          trend={
            apiSummary && (apiSummary.prevTotalCalls ?? 0) > 0
              ? {
                  current: arpCall,
                  previous: (apiSummary.prevNetEarnings ?? 0) / apiSummary.prevTotalCalls,
                }
              : null
          }
        />
        <KpiCard label="待结算" value={overviewLoading ? '…' : fmtTinyUsd(overview?.pendingEarnings)} hint="全服务汇总 · 不随日期筛选" dim={!overviewLoading && (Number(overview?.pendingEarnings) || 0) === 0} />
      </KpiGrid>

      {/* 收入视图：日增量柱状 + 累计折线。两张共用同一 trend 数据，
          一个看「这一天结算多少」，一个看「到今天累计多少」—— 服务商
          每天打开页面想问的两个问题。宽屏并排 2 列。 */}
      <ChartGrid minWidth={420}>
        <Section compact title="净收入时序 · 日">
          {overviewLoading ? (
            <LoadingHint />
          ) : trendPoints.length > 0 ? (
            <BarChart data={trendPoints} valueKey="value" height={160} emptyZeroHint="该时间窗口内没有结算收益" />
          ) : (
            <EmptyBarChart height={160} hint="首次结算后这里会显示净收入曲线" subHint="7 天结算周期" />
          )}
        </Section>
        <Section
          compact
          title="净收入累计"
          action={
            cumulativeTrend.length > 0 ? (
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }} className="mono">
                到期末 {fmtTinyUsd(cumulativeTrend[cumulativeTrend.length - 1].cum)}
              </span>
            ) : null
          }
        >
          {overviewLoading ? (
            <LoadingHint />
          ) : cumulativeTrend.length > 0 ? (
            <LineChart
              data={cumulativeTrend}
              series={[{ key: 'cum', label: '累计', color: 'var(--c-amber)' }]}
              height={160}
              unit=""
            />
          ) : (
            <EmptyBarChart height={160} hint="首次结算后这里会显示累计净收入" subHint="日点位的滚动加和" />
          )}
        </Section>
      </ChartGrid>

      <Section
        title="按 Endpoint 拆解 (毛收入)"
        action={
          <ExportCsvButton
            rows={detail?.endpoints || []}
            filename={`revenue-endpoints-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`}
            columns={[
              { key: 'name', label: 'Endpoint' },
              { key: 'method', label: 'Method' },
              { key: 'path', label: 'Path' },
              { key: 'calls', label: 'Calls' },
              { key: 'totalCost', label: 'Gross USD' },
            ]}
          />
        }
      >
        {overviewLoading ? (
          <LoadingHint />
        ) : detail?.endpoints?.length ? (
          <DataTable>
            <thead>
              <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                <th style={{ padding: '6px 8px' }}>Endpoint</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>毛收入</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
              </tr>
            </thead>
            <tbody>
              {/* 按毛收入降序(并列再按调用量);否则端点按定义顺序排,有收入的被埋在后面。 */}
              {[...detail.endpoints]
                .sort((a, b) => (Number(b.totalCost) - Number(a.totalCost)) || (b.calls - a.calls))
                .map((ep) => {
                const pct = apiGross > 0 ? (Number(ep.totalCost) / apiGross) * 100 : 0;
                return (
                  <tr
                    key={ep.endpointId}
                    style={{ borderTop: '1px solid var(--bd-1)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <MethodPill method={ep.method} />
                        <span style={{ color: 'var(--fg-0)' }}>{ep.name}</span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }} className="mono">{ep.path}</div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(ep.calls)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(ep.totalCost)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        ) : (
          <EmptyTable cols={4} rows={4} hint="首次结算后这里会按 endpoint 拆解收入" />
        )}
      </Section>

      {/* C3 — 按调用方拆解收入：服务商最常问"谁是最大客户" */}
      <Section
        title={`按调用方拆解 (净收入${callersRevenue?.items?.length ? ` · ${callersRevenue.items.length}` : ''})`}
        action={
          callersRevenue?.items?.length > 0 ? (
            <ExportCsvButton
              rows={callersRevenue.items}
              filename={`revenue-by-caller-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`}
              columns={[
                { key: 'callerRef', label: 'Caller reference' },
                { key: 'callerHandle', label: 'Caller' },
                { key: 'calls', label: 'Calls' },
                { key: 'grossAmount', label: 'Gross USD' },
                { key: 'platformFee', label: 'Platform fee USD' },
                { key: 'netAmount', label: 'Net USD' },
                { key: 'firstSeen', label: 'First seen', transform: (v) => new Date(v).toISOString() },
                { key: 'lastSeen', label: 'Last seen', transform: (v) => new Date(v).toISOString() },
              ]}
            />
          ) : null
        }
      >
        {overviewLoading ? (
          <LoadingHint />
        ) : callersRevenue?.items?.length > 0 ? (
          <DataTable>
            <thead>
              <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                <th style={{ padding: '6px 8px' }}>调用方</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>收益记录</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>毛收入</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>净收入</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                <th style={{ padding: '6px 8px' }}>首次</th>
                <th style={{ padding: '6px 8px' }}>最近</th>
              </tr>
            </thead>
            <tbody>
              {callersRevenue.items.slice(0, 25).map((c) => {
                const pct = callersRevenue.totalNet > 0
                  ? (c.netAmount / callersRevenue.totalNet) * 100
                  : 0;
                return (
                  <tr
                    key={c.callerRef}
                    style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                    onClick={() => navigate('workspace-caller', { apiId, callerId: c.callerRef })}
                    title="点击进入该 caller 的 drill-down"
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '6px 8px' }} className="mono" title={c.callerHandle}>
                      {c.callerHandle}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(c.calls)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(c.grossAmount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }} className="mono">{fmtTinyUsd(c.netAmount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>
                      {new Date(c.firstSeen).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>
                      {new Date(c.lastSeen).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        ) : (
          <EmptyTable cols={5} rows={3} hint="首次结算后这里会按调用方拆解" subHint="服务商可一眼看出最大客户" />
        )}
      </Section>

      <Section
        title={`结算明细 (${historyTotal})`}
        action={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>{t('earnings.settlementNote')}</span>
            <ExportCsvButton
              rows={sortedHistory}
              filename={`settlements-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`}
              columns={[
                { key: 'createdAt', label: 'Created', transform: (v) => new Date(v).toISOString() },
                { key: 'status', label: 'Status' },
                { key: 'grossAmount', label: 'Gross USD' },
                { key: 'platformFee', label: 'Platform fee USD' },
                { key: 'netAmount', label: 'Net USD' },
                { key: 'settledAt', label: 'Settled', transform: (v) => v ? new Date(v).toISOString() : '' },
              ]}
            />
          </div>
        }
      >
        {historyError && <div role="alert" style={{ color: 'var(--err)', marginBottom: 12 }}>
          {historyError} <button className="btn btn-sm" onClick={() => fetchHistory({ page: historyItems.length ? historyPage + 1 : 1, append: historyItems.length > 0 })}>重试</button>
        </div>}
        <FilterBar>
          <PillGroup
            label="状态"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
          {statusFilter && (
            <button
              type="button"
              onClick={() => setStatusFilter(null)}
              style={{
                padding: '5px 10px', border: '1px solid var(--bd-1)',
                background: 'transparent', borderRadius: 6,
                color: 'var(--fg-3)', fontSize: 11, cursor: 'pointer',
              }}
            >清除筛选 ×</button>
          )}
        </FilterBar>

        {historyLoading && historyItems.length === 0 ? (
          <LoadingHint />
        ) : sortedHistory.length === 0 ? (
          statusFilter ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
              当前筛选下没有结算记录 ·{' '}
              <button type="button" onClick={() => setStatusFilter(null)} style={{ background: 'none', border: 'none', color: 'var(--fg-1)', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>清除筛选</button>
            </div>
          ) : (
            <EmptyTable cols={5} rows={3} hint="首次结算后这里会显示明细" subHint={t('earnings.settlementNote')} />
          )
        ) : (
          <>
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <SortHeader sortKey="createdAt" currentSort={historySort.key} currentDir={historySort.dir} onSort={onSort}>
                    时间
                  </SortHeader>
                  <th style={{ padding: '6px 8px' }}>状态</th>
                  <SortHeader sortKey="grossAmount" currentSort={historySort.key} currentDir={historySort.dir} onSort={onSort} align="right">
                    毛额
                  </SortHeader>
                  <SortHeader sortKey="platformFee" currentSort={historySort.key} currentDir={historySort.dir} onSort={onSort} align="right">
                    平台费
                  </SortHeader>
                  <SortHeader sortKey="netAmount" currentSort={historySort.key} currentDir={historySort.dir} onSort={onSort} align="right">
                    净额
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {sortedHistory.map((row) => (
                  <tr
                    key={row.id}
                    style={{ borderTop: '1px solid var(--bd-1)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '6px 8px' }} className="mono">{new Date(row.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '6px 8px' }}><SettlementPill status={row.status} /></td>
                    {/* H-fix: transaction-log rows show actual decimal value,
                        not '<$0.01'. Without this every sub-cent settlement
                        renders identically and the table loses information. */}
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(row.grossAmount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--fg-3)' }} className="mono">{fmtTinyUsd(row.platformFee)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }} className="mono">{fmtTinyUsd(row.netAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <TableFooter
              hasMore={hasMore}
              loading={historyLoading}
              totalLoaded={sortedHistory.length}
              onLoadMore={() => fetchHistory({ page: historyPage + 1, append: true })}
              totalLabel={`第 ${historyPage}/${historyTotalPages} 页`}
            />
          </>
        )}
      </Section>
    </div>
  );
};
